import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentModel } from 'src/app/models/payment.model';
import { ReservationModel } from 'src/app/models/reservation.model';
import { PaymentService } from 'src/app/services/payment.service';
import { ReservationService } from 'src/app/services/reservation.service';
import { CurrencyInputDirective } from 'src/app/shared/directives/currency-input.directive';
import { PaymentMethodEnum } from 'src/app/shared/enumerators/payment-method.enum';
import { PaymentStatusEnum } from 'src/app/shared/enumerators/payment-status.enum';
import { DescriptionService } from 'src/app/shared/services/description.service';
import { SwalFireService } from 'src/app/shared/services/swal-fire.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { maxAmountValidator } from 'src/app/shared/validators/custom-validators';

@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    CurrencyInputDirective,
    MatTableModule
  ],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.css'
})

export class PaymentComponent implements OnInit {
  form: FormGroup;
  paymentDisplayedColumns = ['paymentDate', 'paymentMethod', 'amount'];
  paymentMethodEnum = PaymentMethodEnum;
  reservation: ReservationModel;

  maxAllowed = 0;

  paymentMethods = Object.keys(PaymentMethodEnum)
    .filter(key => !isNaN(Number(PaymentMethodEnum[key as any])))
    .map(key => ({
      value: PaymentMethodEnum[key as keyof typeof PaymentMethodEnum],
      label: key
    }));

  constructor(private fb: FormBuilder,
    private reservationSrv: ReservationService,
    private toastr: ToastService,
    public activatedRoute: ActivatedRoute,
    private descriptionService: DescriptionService,
    private readonly swalFire: SwalFireService,
    private router: Router,
    private paymentSrv: PaymentService) { }

  ngOnInit() {
    this.getReservation();
    this.form = this.fb.group({
      amount: [null, [Validators.required, maxAmountValidator(() => this.maxAllowed)]],
      reservationId: [null, Validators.required],
      paymentMethod: [null, Validators.required]
    });
  }

  registerPayment() {
    let model = this.form.value as PaymentModel;
    this.paymentSrv.post(model).subscribe({
      next: (data) => {
        this.form.reset();
        this.getReservation();
        this.reservation?.payments.push(model);
        this.toastr.success(
          'Pagos',
          'El pago se registro correctamente.'
        );

        if (!this.incompletePayment()) {
          this.swalFire.SwalFireQuestion(
            "Reserva",
            "¿Desea registrar los huéspedes?",
            "Completar",
            "Ir al inicio"
          ).then((result) => {
            if (result.isConfirmed) {
              this.router.navigate([`check-in`], { queryParams: { id: data.reservationId } });
            }
            else {
              this.router.navigate(['/booking-list']);
            }
          });
        }
      },
      error: (err) => {
        this.toastr.error(
          'Pagos',
          'Error al registrar el pago.'
        );
      }
    });
  }

  getReservation() {
    this.activatedRoute.queryParams.subscribe((params) => {
      let reservationId = params['id'];
      if (reservationId) {
        this.reservationSrv.getById(reservationId).subscribe({
          next: (data) => {
            this.reservation = data;
            let maxValue = this.reservation.totalPrice - this.reservation.payments.reduce((sum, item) => sum + item.amount, 0);
            this.maxAllowed = maxValue;
            this.form.controls.amount.clearValidators();
            this.form.controls.amount.updateValueAndValidity();
            this.form.controls.amount.setValidators([
              Validators.required,
              maxAmountValidator(() => this.maxAllowed)
            ]);
            this.form.controls.reservationId.setValue(data.id);
          },
          error: (err) => {
            this.toastr.error(
              'Reserva',
              'Error al obtener las reserva.'
            );
          }
        });
      }
    });
  }

  documentTypeLabel(documentType: any): string {
    return this.descriptionService.documentTypeLabel(documentType);
  }

  getTotalAmount(): number {
    return this.reservation?.payments?.reduce((sum, p) => sum + (p.amount ?? 0), 0) || 0;
  }

  incompletePayment() {
    return this.reservation?.totalPrice > this.getTotalAmount();
  }
}
